import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "../../api/axios";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import Select from "react-select";
import EventsTile from "../../components/EventsTile/EventsTile";
import { NoResults } from "../../components/Generic";
import Pagination from "@mui/material/Pagination";
import CircularProgress from "@mui/material/CircularProgress";
import useMediaQuery from "@mui/material/useMediaQuery";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Tooltip from "@mui/material/Tooltip";
import styles from "./EventsAndOpportunities.module.css";
const EventsAndOpportunities = (props) => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const [data, setData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [categoriesList, setCategoriesList] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);
  // Filter toggles: "Show Flexible/Open Dates" defaults ON, "Hide Reserved Events" defaults OFF
  const [showFlexible, setShowFlexible] = useState(true);
  const [hideReserved, setHideReserved] = useState(false);

  // Toggle values sent to the API on every listing request
  const toggleParams = () => ({
    showFlexible: showFlexible ? "true" : "false",
    hideReserved: hideReserved ? "true" : "false",
  });

  // iOS-style pill toggle: the white thumb sits fully INSIDE a tall rounded
  // track (navy when on, gray when off), instead of the default MUI look where
  // the thumb bulges outside a thin track.
  const switchSx = {
    width: 44,
    height: 24,
    padding: 0,
    display: "flex",
    "& .MuiSwitch-switchBase": {
      padding: 0,
      margin: "2px",
      transitionDuration: "200ms",
      // OFF: gray knob with a darker gray border so it stands out on the white track
      "& .MuiSwitch-thumb": {
        backgroundColor: "#e2e6ea",
        border: "1px solid #b8bfc7",
      },
      "&.Mui-checked": {
        transform: "translateX(20px)",
        // ON: white knob against the navy track
        "& .MuiSwitch-thumb": {
          backgroundColor: "#ffffff",
          border: "none",
        },
        "& + .MuiSwitch-track": {
          backgroundColor: "#003063",
          opacity: 1,
          border: 0,
        },
      },
    },
    "& .MuiSwitch-thumb": {
      boxSizing: "border-box",
      width: 20,
      height: 20,
      boxShadow: "0 1px 2px rgba(0,0,0,0.25)",
    },
    "& .MuiSwitch-track": {
      borderRadius: 24 / 2,
      backgroundColor: "#ffffff",
      opacity: 1,
      border: "1px solid rgba(0,0,0,0.2)",
    },
  };

  const getEventsData = async (page = 1) => {
    setIsLoading(true);
    try {
      let eventData = await axios.get("/api/events", {
        params: { page: page, limit: 20, ...toggleParams() }
      });
      let categoryData = await axios.get("/api/events/eventcategories");
      
      //console.log('Event and Category Data: ', eventData, categoryData);
      setData(eventData.data.events);
      
      // Add "View All" option at the beginning of categories list
      const categoriesWithViewAll = [
        { CategoryID: 'all', CategoryDisplayName: 'View All', value: 'all' },
        ...categoryData.data
      ];
      setCategoriesList(categoriesWithViewAll);
      
      setPageCount(eventData.data.pagination.totalPages);
      setCurrentPage(eventData.data.pagination.currentPage);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = useCallback(async (event, page) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    if (isSearchActive && searchQuery.trim()) {
      // If search is active, fetch search results for the new page
      setIsLoading(true);
      try {
        const response = await axios.get("/api/events/search", {
          params: {
            q: searchQuery,
            page: page,
            limit: 20,
            ...toggleParams()
          }
        });

        setData(response.data.events);
        setPageCount(response.data.pagination.totalPages);
        setCurrentPage(response.data.pagination.currentPage);
      } catch (error) {
        console.error('Error searching events:', error);
        setData([]);
        setPageCount(0);
      } finally {
        setIsLoading(false);
      }
    } else if (selectedCategory) {
      // If a category is selected, fetch filtered events
      await getEventsByCategory(selectedCategory, page);
    } else {
      // Otherwise fetch all events
      await getEventsData(page);
    }
  }, [isSearchActive, searchQuery, selectedCategory, showFlexible, hideReserved]);

  const getEventsByCategory = async (categoryId, page = 1) => {
    setIsLoading(true);
    try {
      let filteredEventsData = await axios.get(`/api/events/eventsbycat/${categoryId}`, {
        params: { page: page, limit: 20, ...toggleParams() }
      });
      
      //console.log('Filtered events data:', filteredEventsData);
      setData(filteredEventsData.data.events);
      setPageCount(filteredEventsData.data.pagination.totalPages);
      setCurrentPage(filteredEventsData.data.pagination.currentPage);
    } catch (error) {
      console.error('Error fetching events by category:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getEventsData();
  }, []);

  // Re-fetch the current view (all / search / category) at page 1 when a toggle changes.
  // Skips the initial mount, which is handled by the effect above.
  const didMountToggles = React.useRef(false);
  useEffect(() => {
    if (!didMountToggles.current) {
      didMountToggles.current = true;
      return;
    }
    setCurrentPage(1);
    if (isSearchActive && searchQuery.trim()) {
      handleSearch();
    } else if (selectedCategory) {
      getEventsByCategory(selectedCategory, 1);
    } else {
      getEventsData(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFlexible, hideReserved]);

  // Handle filtering by category from URL query parameter (after categories are loaded)
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (categoryParam && categoriesList.length > 0) {
      const categoryToFilter = categoriesList.find(
        cat => cat.CategoryDisplayName === categoryParam
      );
      
      if (categoryToFilter && categoryToFilter.CategoryID !== 'all') {
        setSelectedCategory(categoryToFilter.CategoryID);
        getEventsByCategory(categoryToFilter.CategoryID, 1);
      }
    }
  }, [categoriesList, searchParams]);

  const handleCategoryChange = async (option) => {
    //console.log("option", option);
    
    // If option is null (cleared) or "View All", reset to all events
    if (!option || option.value === 'all') {
      setSelectedCategory(null);
      setCurrentPage(1);
      setIsSearchActive(false);
      setSearchQuery("");
      navigate('/events', { replace: true });
      await getEventsData(1);
      return;
    }
    
    setSelectedCategory(option.CategoryID);
    setCurrentPage(1);
    setIsSearchActive(false);
    setSearchQuery("");
    navigate(`/events?category=${encodeURIComponent(option.CategoryDisplayName)}`, { replace: true });
    await getEventsByCategory(option.CategoryID, 1);
  };

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Reset page when search changes
    setCurrentPage(1);
  }, []);

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) {
      return;
    }
    
    setIsLoading(true);
    setIsSearchActive(true);
    
    try {
      const response = await axios.get("/api/events/search", {
        params: {
          q: searchQuery,
          page: 1,
          limit: 20,
          ...toggleParams()
        }
      });

      setData(response.data.events);
      setPageCount(response.data.pagination.totalPages);
      setCurrentPage(response.data.pagination.currentPage);
      setSelectedCategory(null);
    } catch (error) {
      console.error('Error searching events:', error);
      setData([]);
      setPageCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, showFlexible, hideReserved]);

  const handleClearSearch = useCallback(() => {
    setSearchQuery("");
    setIsSearchActive(false);
    setCurrentPage(1);
    getEventsData(1);
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  }, [handleSearch]);

  return (
    <div>
      <div className={styles.filtering}>
        <Select
          placeholder="Select Categories"
          getOptionLabel={(options) => options["CategoryDisplayName"]}
          getOptionValue={(options) => options["CategoryID"]}
          options={categoriesList}
          onChange={handleCategoryChange}
          className={styles.select}
          value={
            categoriesList.find(cat => 
              cat.CategoryID === selectedCategory || 
              cat.CategoryDisplayName === searchParams.get('category')
            ) || null
          }
          isClearable
        />
        <div className={styles.searchContainer}>
          <input
            name="search"
            type="text"
            maxLength="200"
            className={styles.input}
            placeholder="Search Events..."
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDown}
          />
          <button 
            className={styles.searchButton}
            onClick={handleSearch}
            disabled={!searchQuery.trim()}
            title="Search"
          >
            <i className="fas fa-search"></i>
          </button>
          {isSearchActive && (
            <button
              className={styles.clearButton}
              onClick={handleClearSearch}
              title="Clear Search"
            >
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>

        <div className={styles.toggles}>
          <div className={styles.toggleItem}>
            <FormControlLabel
              control={
                <Switch
                  checked={showFlexible}
                  onChange={(e) => setShowFlexible(e.target.checked)}
                  sx={switchSx}
                />
              }
              label="Show Flexible/Open Dates"
              className={styles.toggleLabel}
            />
            <Tooltip title="Include events with flexible or open dates instead of a fixed date.">
              <i className={`fas fa-info-circle ${styles.toggleInfo}`}></i>
            </Tooltip>
          </div>

          <div className={styles.toggleItem}>
            <FormControlLabel
              control={
                <Switch
                  checked={hideReserved}
                  onChange={(e) => setHideReserved(e.target.checked)}
                  sx={switchSx}
                />
              }
              label="Hide Reserved Events"
              className={styles.toggleLabel}
            />
            <Tooltip title="Hide events that are already reserved or full.">
              <i className={`fas fa-info-circle ${styles.toggleInfo}`}></i>
            </Tooltip>
          </div>
        </div>
      </div>
      
      {isLoading ? (
        <div className={styles.loading}>
          <CircularProgress size={24} sx={{ color: '#003063', mr: 2 }} />
          <span style={{ marginTop: '1px' }}>Loading events...</span>
        </div>
      ) : data?.length > 0 ? (
        <>
          <div className={styles.eventsListContainer}>
            {data.map((singleEvent) => (
              <EventsTile
                key={singleEvent.EventsID}
                Event={singleEvent}
                Categories={categoriesList}
              />
            ))}
          </div>
          
          {pageCount > 1 && (
            <div className={styles.paginationWrapper}>
              <Pagination
                count={pageCount}
                page={currentPage}
                variant="outlined"
                shape="rounded"
                className={styles.pagination}
                onChange={handlePageChange}
                showFirstButton
                showLastButton
                siblingCount={isMobile ? 0 : 1}
                boundaryCount={1}
              />
            </div>
          )}
        </>
      ) : (
        <div className={styles.noResults}>
          <NoResults
            title="No Events Found"
            description="Please try again later."
          />
        </div>
      )}
    </div>
  );
};

export default EventsAndOpportunities;
